import React from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { FaRegCalendarAlt } from "react-icons/fa"; // using react-icons

// Define Color Mapping for Ratings
const ratingColors = {
  BE: "#FF3B3B",  // Below Expectations - Red
  ME: "#FFD700",  // Meeting Expectations - Gold
  EE: "#28A745"   // Exceeding Expectations - Green
};

const PieChartComponent = ({ kpiRatingEmployeeCount, selectedMonth, selectedYear }) => {
  const chartData = kpiRatingEmployeeCount.length > 0 
    ? kpiRatingEmployeeCount.map(item => ({
        name: item._id,
        value: item.count,
        color: ratingColors[item._id]
      }))
    : [
        { name: "BE", value: 0, color: ratingColors["BE"] },
        { name: "ME", value: 0, color: ratingColors["ME"] },
        { name: "EE", value: 0, color: ratingColors["EE"] }
      ];

  const monthName = new Date(0, selectedMonth - 1).toLocaleString('default', { month: 'long' });

  return (
    <div className="flex flex-col items-center justify-center p-10 bg-white shadow-2xl rounded-2xl w-full max-w-2xl mx-auto relative">
      <style>
        {`
          @keyframes gradient-rotate {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .highlight-badge {
            background: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(10px);
            border-radius: 9999px;
            padding: 0.5rem 1rem;
            display: inline-flex;
            align-items: center;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border: 3px solid transparent;
            background-clip: padding-box, border-box;
            background-origin: border-box;
            background-image: linear-gradient(#fff, #fff), linear-gradient(270deg, #1e3a8a, #dc2626, #1e3a8a);
            background-size: 200% 200%;
            animation: gradient-rotate 4s linear infinite;
          }
          .highlight-badge:hover {
            transform: scale(1.05);
            box-shadow: 0 4px 20px rgba(0,0,0,0.2);
          }
        `}
      </style>

      {/* Title */}
      <h2 className="text-3xl font-extrabold text-gray-900 mb-2 text-center uppercase tracking-wide">
        📊 Overall KPI Rating
      </h2>

      {/* Highlighted Filter Info with Icon */}
      <div className="mb-6">
        <span className="highlight-badge text-blue-700 font-semibold text-sm md:text-base">
          <FaRegCalendarAlt className="mr-2 text-blue-700" />
          {monthName} {selectedYear}
        </span>
      </div>

      {/* Responsive Pie Chart */}
      <div className="w-full h-96">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={90}
              outerRadius={140}
              paddingAngle={5}
              dataKey="value"
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              labelLine={false}
              animationDuration={800}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} stroke="#fff" strokeWidth={3} />
              ))}
            </Pie>
            <Tooltip />
            <Legend verticalAlign="bottom" iconType="circle" />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Notes Section */}
      <div className="mt-8 bg-gray-100 p-6 rounded-lg w-full text-center shadow-md">
        <h3 className="text-xl font-bold text-gray-800 mb-4">📝 Rating Explanation</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center justify-center">
            <span className="w-5 h-5 bg-red-500 inline-block rounded-full mr-2"></span>
            <span className="text-gray-700 font-medium">Below Expectations</span>
          </div>
          <div className="flex items-center justify-center">
            <span className="w-5 h-5 bg-yellow-500 inline-block rounded-full mr-2"></span>
            <span className="text-gray-700 font-medium">Meeting Expectations</span>
          </div>
          <div className="flex items-center justify-center">
            <span className="w-5 h-5 bg-green-500 inline-block rounded-full mr-2"></span>
            <span className="text-gray-700 font-medium">Exceeding Expectations</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PieChartComponent;
